import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Play, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useHub } from "@/components/AppShell";
import { EmptyState, Loader, PageHeader } from "@/components/common";
import { useLiveRefresh } from "@/lib/live";
import { MilestoneDialog, Viewer, src } from "@/components/memories/MemoryParts";

const ORANGE = "var(--glow-home)";
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function Tile({ m, onOpen }) {
  return (
    <button onClick={onOpen} className="group relative aspect-square overflow-hidden rounded-xl bg-q-raised" data-testid={`memory-${m.id}`} aria-label={m.caption || `Memory from ${fmtDate(m.date)}`}>
      {m.thumb ? <img src={src(m.thumb)} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" /> : <span className="flex h-full items-center justify-center text-3xl">🎬</span>}
      {m.kind === "video" && <Play className="absolute right-2 top-2 h-5 w-5 fill-white text-white drop-shadow" />}
    </button>
  );
}

function MilestoneCard({ s, onEdit, onOpen }) {
  return (
    <div className="q-card col-span-full flex flex-wrap gap-3 p-3 sm:col-span-3" style={{ borderColor: `rgba(${ORANGE},0.45)` }} data-testid={`milestone-${s.id}`}>
      <span className="text-3xl">{s.emoji}</span>
      <div className="min-w-0 flex-1">
        <p className="font-display font-extrabold text-q-text">{s.title}</p>
        <p className="text-xs text-q-muted">{fmtDate(s.date)}{s.tags.length ? ` · ${s.tags.map((t) => t.name.split(" ")[0]).join(", ")}` : ""}</p>
        {s.note && <p className="mt-1 text-sm text-q-text">{s.note}</p>}
        {s.photos.length > 0 && <div className="mt-2 flex gap-1">{s.photos.slice(0, 4).map((p, i) => <button key={p.id} onClick={() => onOpen(s.photos, i)} className="h-14 w-14 overflow-hidden rounded-lg"><img src={src(p.thumb)} alt="" className="h-full w-full object-cover" /></button>)}</div>}
      </div>
      {s.can_edit && <button className="q-btn q-btn-ghost min-h-[44px]" onClick={onEdit} data-testid={`milestone-edit-${s.id}`}>Edit</button>}
    </div>
  );
}

function groupByMonth(items) {
  const out = [];
  for (const it of items) {
    const y = it.date.slice(0, 4); const mo = Number(it.date.slice(5, 7));
    let g = out[out.length - 1];
    if (!g || g.y !== y || g.mo !== mo) { g = { y, mo, items: [] }; out.push(g); }
    g.items.push(it);
  }
  return out;
}

export default function Memories() {
  const { hub } = useHub();
  const [d, setD] = useState(null);
  const [items, setItems] = useState([]);
  const [person, setPerson] = useState("");
  const [view, setView] = useState(null);
  const [ms, setMs] = useState(null);
  const [years, setYears] = useState([]);
  const more = useRef(null);
  const loading = useRef(false);
  const load = useCallback(() => api.get(`/organizations/${hub.id}/memories`, { params: { person } }).then(({ data }) => { setD(data); setItems(data.items); }).catch((e) => toast.error(errMsg(e))), [hub.id, person]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get(`/organizations/${hub.id}/memories/years`).then(({ data }) => setYears(data.years)).catch(() => {}); }, [hub.id]);
  useLiveRefresh(load, (m) => m.entity === "memory", 30000);
  const next = d?.next_offset;
  useEffect(() => {
    if (next == null || !more.current) return undefined;
    const ob = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting || loading.current) return;
      loading.current = true;
      api.get(`/organizations/${hub.id}/memories`, { params: { person, offset: next } }).then(({ data }) => { setItems((x) => [...x, ...data.items]); setD(data); }).finally(() => { loading.current = false; });
    }, { rootMargin: "600px" });
    ob.observe(more.current);
    return () => ob.disconnect();
  }, [next, hub.id, person]);
  if (!d) return <Loader />;
  const photos = items.filter((x) => x.type === "memory");
  const groups = groupByMonth(items);
  let lastYear = null;
  return (
    <div data-testid="memories-page">
      <PageHeader eyebrow="Home" title="Memories" rgb={ORANGE} testid="memories-title" />
      <div className="mb-5 flex flex-wrap items-center gap-2">
        {d.can_add && <button className="q-btn q-btn-primary min-h-[44px]" onClick={() => setMs({})} data-testid="milestone-add-btn"><Plus className="h-4 w-4" /> Add milestone</button>}
        <select className="q-input min-h-[44px] w-auto max-w-full" value={person} onChange={(e) => setPerson(e.target.value)} data-testid="memories-person-filter"><option value="">Everyone</option>{d.people.map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}</select>
        {years.map((y) => <Link key={y} to={`/h/${hub.id}/memories/year/${y}`} className="q-btn q-btn-secondary min-h-[44px]" data-testid={`year-review-link-${y}`}><Sparkles className="h-4 w-4" /> {y} in review</Link>)}
      </div>
      {!items.length && <EmptyState title="No memories yet" hint="Photos from the family album and the Whole family chat show up here by themselves." rgb={ORANGE} testid="memories-empty" />}
      {groups.map((g) => {
        const showYear = g.y !== lastYear; lastYear = g.y;
        return (
          <section key={`${g.y}-${g.mo}`} className="mb-6" data-testid={`month-${g.y}-${g.mo}`}>
            {showYear && <h2 className="mb-3 font-display text-3xl font-extrabold text-q-text" data-testid={`year-${g.y}`}>{g.y}</h2>}
            <h3 className="mb-2 text-xs font-black uppercase tracking-[0.18em]" style={{ color: `rgb(${ORANGE})` }}>{MONTHS[g.mo - 1]}</h3>
            <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-4 lg:grid-cols-6">
              {g.items.map((it) => (it.type === "milestone"
                ? <MilestoneCard key={it.id} s={it} onEdit={() => setMs(it)} onOpen={(list, i) => setView({ list, i })} />
                : <Tile key={it.id} m={it} onOpen={() => setView({ list: photos, i: photos.findIndex((p) => p.id === it.id) })} />))}
            </div>
          </section>
        );
      })}
      {next != null && <div ref={more} className="py-6"><Loader label="Loading more…" /></div>}
      {view && <Viewer hubId={hub.id} people={d.people} items={view.list} index={view.i} onClose={() => setView(null)} onChanged={load} />}
      {ms && <MilestoneDialog hubId={hub.id} people={d.people} ms={ms.id ? ms : null} onClose={() => setMs(null)} onSaved={() => { setMs(null); load(); }} />}
    </div>
  );
}
